// Helpers de fechas compartidos entre features del frontend.
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/es';

export const DATE_FORMAT = 'DD/MM/YYYY';

function isUtcMidnight(d: Date): boolean {
  return (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  );
}

/** UTC calendar components as YYYY-MM-DD (the date a UTC-midnight value represents). */
function utcCalendarDate(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate(),
  ).padStart(2, '0')}`;
}

/**
 * Date-only contract (see `DateOnlyString` in @aid/shared): parses a date-only
 * value into a LOCAL-midnight Dayjs.
 *
 * - `YYYY-MM-DD` strings are parsed component-wise as local midnight (dayjs
 *   never treats a date-only string as UTC), so the calendar date is exact in
 *   every timezone.
 * - Legacy/API values serialized as ISO midnight (`2026-09-01T00:00:00.000Z`
 *   or a Date at UTC midnight) recover the intended calendar date from their
 *   UTC components — a UTC-negative browser (America/Guatemala) can never
 *   render 31/08/2026 for 01/09/2026.
 * - Real instants that are NOT at UTC midnight pass through as exact times.
 *
 * Intended for DATE-backed response fields (fechaNacimiento, fechaCobro,
 * periods, visits). Real instants (timestamptz fields such as fechaCreacion
 * and fechaPago) must NOT be routed through this helper. The ONE exception is
 * the timestamptz fechaAlta, whose edit field is date-only — use
 * `parseFechaAlta` (same recovery rules, documented for that contract).
 */
export function parseDateOnly(value: Date | string | null | undefined): Dayjs | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  const instant = value instanceof Date ? value : dayjs(value).toDate();
  if (Number.isNaN(instant.getTime())) return null;
  if (isUtcMidnight(instant)) {
    return dayjs(utcCalendarDate(instant));
  }
  const parsed = dayjs(instant);
  return parsed.isValid() ? parsed : null;
}

/**
 * FechaAlta UI contract: `fecha_alta` is a timestamptz column, but the edit
 * form treats it as a date-only field (the legacy business value is a
 * calendar day, and migration 0012 canonicalized it as Guatemala midnight).
 * Both shapes must render the intended date:
 *
 * - Legacy/buggy rows at exact UTC midnight (`2026-01-05T00:00:00.000Z`):
 *   recover the UTC calendar components (Jan 5) — a naive `dayjs()` would
 *   render Jan 4 in America/Guatemala.
 * - Canonical post-0012 values at Guatemala midnight
 *   (`2026-01-05T06:00:00.000Z`) and any real instant: preserved exactly
 *   (the instant's calendar date is the UI date in the business timezone).
 *
 * On submit the field serializes with `toDateOnlyString`; the backend maps
 * `YYYY-MM-DD` back to the business-zone midnight instant
 * (`businessMidnightInstant` in apps/aid-backend/src/core/utils/fechas.ts).
 */
export function parseFechaAlta(value: Date | string | null | undefined): Dayjs | null {
  return parseDateOnly(value);
}

/** Serializes a Dayjs date-only value back to `YYYY-MM-DD` (calendar-based, timezone-safe). */
export function toDateOnlyString(value: Dayjs | null | undefined): string | undefined {
  return value?.format('YYYY-MM-DD');
}

/**
 * Formatea el período que constituye una factura o cubre un pago como
 * "DD/MM/YYYY – DD/MM/YYYY" (o con el formato corto indicado, p. ej.
 * 'DD/MM' para "06/07 – 06/08"). Devuelve null cuando falta alguno de los
 * extremos (facturas/pagos legacy sin período).
 */
export function formatearPeriodo(
  fechaInicio: Date | string | null,
  fechaFin: Date | string | null,
  formato = DATE_FORMAT,
): string | null {
  if (!fechaInicio || !fechaFin) return null;
  const inicio = parseDateOnly(fechaInicio);
  const fin = parseDateOnly(fechaFin);
  if (!inicio || !fin) return null;
  return `${inicio.format(formato)} – ${fin.format(formato)}`;
}

function normalizeDateLocale(locale: string): 'en' | 'es' {
  return locale.toLowerCase().startsWith('en') ? 'en' : 'es';
}

function parseLocalizedDate(value: Date | string | null | undefined, locale: string) {
  const parsed = parseDateOnly(value);
  return parsed ? parsed.locale(normalizeDateLocale(locale)) : null;
}

function withoutMonthPunctuation(value: string): string {
  return value.replace(/\./g, '');
}

/** Compact, localized date for dense operational tables. */
export function formatCompactDate(
  value: Date | string | null | undefined,
  locale = 'es',
): string | null {
  const parsed = parseLocalizedDate(value, locale);
  return parsed ? withoutMonthPunctuation(parsed.format('D MMM YYYY')) : null;
}

/** Compact invoice period that omits the repeated start year only within one year. */
export function formatearPeriodoCompacto(
  fechaInicio: Date | string | null | undefined,
  fechaFin: Date | string | null | undefined,
  locale = 'es',
): string | null {
  const inicio = parseLocalizedDate(fechaInicio, locale);
  const fin = parseLocalizedDate(fechaFin, locale);
  if (!inicio || !fin) return null;

  const sameYear = inicio.year() === fin.year();
  const startFormat = sameYear ? 'D MMM' : 'D MMM YYYY';
  return `${withoutMonthPunctuation(inicio.format(startFormat))} → ${withoutMonthPunctuation(
    fin.format('D MMM YYYY'),
  )}`;
}
